import React, { useEffect, useState } from "react";
import { Wrapper, Container, Body, HeaderInfo } from "../../style/view.styles";

import {
  Section,
  SectionTitle,
  SectionContent,
  DashboardLayout,
  TopSection,
  DashboardCard,
  CardContent,
  BottomSection,
  ApplicationsTable,
  InterviewsContainer,
  InterviewCard,
} from "../../style/talent.dashboard.styles";
import LoadingSpinner from "../../components/Elements/LoadingSpinner";
import EmptyState from "../../components/Elements/EmptyState";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { fetchEntityData } from "../../utils/RequestHandler";
import { getLoggedInTalent, getLoggedInUser } from "../../utils/AuthHandler";
import { calculateCompletionStatus } from "../../utils/Timer";

export default function TalentDashboard() {
  const [loading, setLoading] = useState(true);
  const [opportunities, setOpportunities] = useState(0);
  const [activeApplications, setActiveApplications] = useState(0);
  const [shortlisted, setShortlisted] = useState(0);
  const [recentApplications, setRecentApplications] = useState([]);
  const [scheduledInterviews, setScheduledInterviews] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileProgress, setProfileProgress] = useState({
    completed: 0,
    incomplete: 0,
  });

  useEffect(() => {
    const checkUserLoggedIn = async () => {
      const user = getLoggedInUser();
      const profile = await getLoggedInTalent(user);
      if (user) {
        setIsLoggedIn(true);
        setProfile(profile);
        setProfileProgress(calculateCompletionStatus(profile));
      } else {
        setIsLoggedIn(false);
      }
    };
    checkUserLoggedIn();

    // TODO: Need to setup these values ASAP
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const fetchOpportunities = async () => {
        const data = await fetchEntityData("opportunities", false);
        if (data.success) {
          const records = data.result;
          setOpportunities(records.length);
        }
      };

      const fetchApplications = async () => {
        const data = await fetchEntityData("job_applications", true);
        if (data.success) {
          const records = data.result;
          setActiveApplications(records.length);
          setRecentApplications(records);
        }
      };

      const fetchData = async () => {
        try {
          setLoading(true);
          await fetchApplications();
          await fetchOpportunities();
        } catch (error) {
          console.error("Failed to fetch data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();

      // TODO: Need to remove dummy ASAP
      setShortlisted(0);
      setScheduledInterviews([]);
    }
  }, [isLoggedIn]);

  const pieData = [
    { name: "Completed", value: profileProgress.completed },
    { name: "Incomplete", value: profileProgress.incomplete },
  ];

  const COLORS = ["#00b894", "#e0e0e0"];

  const getStatusClass = (status) => {
    switch (status) {
      case "applied":
        return "status-applied";
      case "interview":
        return "status-interview";
      case "shortlisted":
        return "status-shortlisted";
      default:
        return "";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "applied":
        return "Applied";
      case "interview":
        return "Interview";
      case "shortlisted":
        return "Shortlisted";
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Wrapper className="paleBlueGreyBg">
      <Container>
        <Body style={{ width: "100%" }}>
          <HeaderInfo>
            <h1 className="font30 darkGreenColor extraBold">
              {profile &&
                profile.first_name &&
                `Welcome, ${profile.first_name}`}
              {!profile && `Welcome,`}
            </h1>
          </HeaderInfo>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <DashboardLayout>
              {/* Top Section with 4 Cards */}
              <Section>
                <TopSection>
                  {/* This Week Opportunities Card */}
                  <DashboardCard>
                    <h3>This Week Opportunities</h3>
                    <CardContent>
                      <div className="card-count">{opportunities}</div>
                      <div className="card-description">
                        Recently added opportunities
                      </div>
                    </CardContent>
                  </DashboardCard>

                  {/* Active Applications Card */}
                  <DashboardCard>
                    <h3>Active Applications</h3>
                    <CardContent>
                      <div className="card-count">{activeApplications}</div>
                      <div className="card-description">
                        Applications in progress
                      </div>
                    </CardContent>
                  </DashboardCard>

                  {/* Shortlisted Card */}
                  <DashboardCard>
                    <h3>Shortlisted</h3>
                    <CardContent>
                      <div className="card-count">{shortlisted}</div>
                      <div className="card-description">
                        You've been shortlisted for these positions
                      </div>
                    </CardContent>
                  </DashboardCard>

                  {/* Profile Completion Pie Chart */}
                  <DashboardCard>
                    <h3>Profile Completion</h3>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={120}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={30}
                            outerRadius={50}
                            fill="#8884d8"
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="card-description">
                        {profileProgress.completed}% Complete{" "}
                        {profileProgress.completed < 100 &&
                          `- Add missing
                        information`}
                      </div>
                    </CardContent>
                  </DashboardCard>
                </TopSection>
              </Section>

              {/* Bottom Section with Table and Interviews */}
              <BottomSection>
                {/* Recent Applications Table */}
                <Section>
                  <SectionTitle>Recent Applications</SectionTitle>
                  <SectionContent>
                    {recentApplications.length > 0 ? (
                      <ApplicationsTable>
                        <thead>
                          <tr>
                            <th>Position</th>
                            <th>Organization</th>
                            <th>Date</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentApplications.map((app) => (
                            <tr key={app.id}>
                              <td>{app.opportunity}</td>
                              <td>{app.organization}</td>
                              <td>{formatDate(app.added_on)}</td>
                              <td>
                                <span
                                  className={`status ${getStatusClass(
                                    app.status
                                  )}`}
                                >
                                  {getStatusText(app.status)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </ApplicationsTable>
                    ) : (
                      <EmptyState message="No recent applications." />
                    )}
                  </SectionContent>
                </Section>

                {/* Scheduled Interviews */}
                <Section>
                  <SectionTitle>Scheduled Interviews</SectionTitle>
                  <SectionContent>
                    {scheduledInterviews.length > 0 ? (
                      <InterviewsContainer>
                        {scheduledInterviews.map((interview) => (
                          <InterviewCard key={interview.id}>
                            <div className="interview-date">
                              {formatDate(interview.date)}
                            </div>
                            <div className="interview-title">
                              {interview.position}
                            </div>
                            <div className="interview-company">
                              {interview.company}
                            </div>
                            <div className="interview-time">
                              {interview.time} • {interview.type}
                            </div>
                          </InterviewCard>
                        ))}
                      </InterviewsContainer>
                    ) : (
                      <EmptyState message="No upcoming interviews scheduled." />
                    )}
                  </SectionContent>
                </Section>
              </BottomSection>
            </DashboardLayout>
          )}
        </Body>
      </Container>
    </Wrapper>
  );
}
